import { FC, useCallback, useMemo, useRef, useState } from 'react'
import {
    createColumnHelper,
    Row,
    SortingState,
    Table,
} from '@atbay/cyber-components'

import { issueTypeToDrawerContentTypeMapping } from '@/core/context/DrawerContentTypes'
import { useDrawer } from '@/core/context/DrawerContext'
import useVirtualizedRowsHeight from '@/core/hooks/useVirtualizedRowsHeight/useVirtualizedRowsHeight'
import { getIssueAnalyticsProps } from '@/core/modules/issues/analytics'
import { EIssueSeverity } from '@/core/modules/issues/constants'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'
import Typography from '@/ui/components/typography'
import { SEVERITY_TEXT_MAPPING } from '@/ui/routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { Analytics } from '@core/services/mixpanel'
import ChevronRight from '@/assets/images/chevron-right.svg?react'

import {
    ChevronCell,
    EmptyCard,
    IssuesTable,
    IssuesTableWrapper,
    SeverityCell,
} from './AssociatedIssuesTable.styles'
import { AssociatedIssuesTableProps } from './AssociatedIssuesTable.types'

type CellInfo = {
    row: Row<TIssue>
    table: Table<TIssue>
}

const columnHelper = createColumnHelper<TIssue>()

const ROW_HEIGHT = 60
const DEFAULT_MAX_VISIBLE_ROWS = 4

const AssociatedIssuesTable: FC<AssociatedIssuesTableProps> = ({
    issues,
    dataTestId,
    maxVisibleRows = DEFAULT_MAX_VISIBLE_ROWS,
    origin,
}) => {
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'severity', desc: true },
    ])
    const { setDrawerState } = useDrawer()

    const wrapperRef = useRef<HTMLDivElement>(null)
    const visibleRowCount = Math.min(issues.length, maxVisibleRows)
    const tableHeight = useVirtualizedRowsHeight(
        wrapperRef,
        maxVisibleRows,
        ROW_HEIGHT * visibleRowCount
    )

    const handleRowClick = useCallback(
        (row: Row<TIssue>, table: Table<TIssue>) => {
            const { sortedIndex, items } = getTableRowContext(row, table)
            const issue = items[sortedIndex]
            const drawerContentType =
                issueTypeToDrawerContentTypeMapping[issue.issue_type]
            if (!drawerContentType) return

            Analytics.event('SEM::user_clicks_at_issue', {
                ...getIssueAnalyticsProps(issue),
                origin,
            })

            setDrawerState((prev) => ({
                ...prev,
                isDrawerOpen: true,
                drawerContentType,
                contentProps: { issues: items, index: sortedIndex },
                previousState: prev.drawerContentType
                    ? {
                          drawerContentType: prev.drawerContentType,
                          contentProps: prev.contentProps,
                          previousState: prev.previousState,
                      }
                    : undefined,
            }))
        },
        [setDrawerState, origin]
    )

    const columns = useMemo(
        () => [
            {
                ...columnHelper.accessor('severity', {
                    id: 'severity',
                    header: () => (
                        <Typography variant="s-regular">Severity</Typography>
                    ),
                    cell: (info) => {
                        const severity = info.getValue()
                        return (
                            <SeverityCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid={`associated-issue-severity-${severity}`}
                            >
                                <SeverityIcon severity={severity} />
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                >
                                    {SEVERITY_TEXT_MAPPING[severity]}
                                </Typography>
                            </SeverityCell>
                        )
                    },
                    enableSorting: true,
                    sortingFn: (a, b) =>
                        (EIssueSeverity[a.original.severity] ?? 0) -
                        (EIssueSeverity[b.original.severity] ?? 0),
                }),
                width: '20%',
            },
            {
                ...columnHelper.accessor('name', {
                    id: 'name',
                    header: () => (
                        <Typography variant="s-regular">Issues Name</Typography>
                    ),
                    cell: (info) => (
                        <div
                            onClick={() => handleRowClick(info.row, info.table)}
                        >
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                testId="associated-issue-name"
                                noWrap={false}
                            >
                                {info.getValue()}
                            </Typography>
                        </div>
                    ),
                    enableSorting: true,
                }),
                width: '75%',
            },
            {
                id: 'chevron',
                header: () => null,
                cell: (info: CellInfo) => (
                    <ChevronCell
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <ChevronRight />
                    </ChevronCell>
                ),
                enableSorting: false,
                width: '5%',
            },
        ],
        [handleRowClick]
    )

    if (issues.length === 0) {
        return (
            <IssuesTableWrapper data-testid={dataTestId}>
                <EmptyCard data-testid="no-open-issues">
                    <Typography variant="s-regular" color="inherit">
                        No open issues.
                    </Typography>
                </EmptyCard>
            </IssuesTableWrapper>
        )
    }

    return (
        <IssuesTableWrapper ref={wrapperRef} data-testid={dataTestId}>
            <IssuesTable
                columns={columns}
                data={issues}
                sorting={sorting}
                setSorting={setSorting}
                getRowId={(row: TIssue) => row.id}
                rowHeight={ROW_HEIGHT}
                tableHeight={tableHeight}
            />
        </IssuesTableWrapper>
    )
}

export default AssociatedIssuesTable
